/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { computed, inject } from "@angular/core";
// Import Angular services used in this class
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { CacheManagerService } from "@app/@services/shared/cache-manager.service";
import { LoggerService } from "@app/@services/shared/logger.service";
import { TRPCService } from "@app/@services/shared/trpc.service";

// Import types and operators from Supabase/PostgREST libraries and RxJS
import type { PostgrestFilterBuilder } from "@supabase/postgrest-js";
import {
	type Observable,
	catchError,
	defer,
	from,
	lastValueFrom,
	map,
	of,
	tap,
} from "rxjs";
import * as uuid from "uuid";

// Import Database type and classes from our project schema
import type { Database } from "../../../../../@schema/db";
import {
	Supabase,
	type Tables,
	type TablesKeys,
	type Views,
	type ViewsKeys,
} from "../supabase";

// Import filter types that describe the filtering structure/interface.
import type {
	FilterType,
	FilterTypeWithoutAggregation,
	FiltersInput,
} from "./items-service-interface";

// ---------------------------------------------------------------------------------
// Type Definitions
// ---------------------------------------------------------------------------------

export type CbRealtimeUpdate = {
	type: "INSERT" | "UPDATE" | "DELETE" | "GENERIC";
	data: Record<string, unknown>;
	table: string;
};

// Define the allowed join types (also add more join types supported by PostgREST as needed).
type JoinType = "inner" | "left" | "right";

// SubSelectQueryComponent holds a sub–query for embedded tables along with join type and application flag.
type SubSelectQueryComponent = MainSelectQueryComponent & {
	table: string;
	join?: JoinType;
	apply: "always" | "onFilter";
	foreignKey?: string; // Optional foreign key specification for multiple FK relationships
};

// MainSelectQueryComponent holds the top‐level select query string and optional array of embedded subqueries.
export type MainSelectQueryComponent = {
	query: string;
	subtables?: SubSelectQueryComponent[];
};

// ---------------------------------------------------------------------------------
// ItemsGenericServiceBase Class Definition
// ---------------------------------------------------------------------------------

/**
 * Abstract ItemsGenericServiceBase class for working with Supabase. It is generic over
 * an Entity that can be a Table or a View. This class encapsulates common functionality for
 * filtering, embedded select query building, multi-tenancy, logging, and more.
 */
export abstract class ItemsGenericServiceBase<
	Entity extends Tables<TablesKeys> | Views<ViewsKeys>,
> extends Supabase {
	// The basic select query (by default, select all columns "*")
	protected readonly selectQuery: MainSelectQueryComponent = {
		query: "*",
	};

	// Abstract function to return a default item of type Entity
	abstract defaultItem(): Entity;

	// Inject the required services using Angular's inject() function
	protected readonly loggerService = inject(LoggerService);
	protected readonly cacheManagerService = inject(CacheManagerService);
	protected readonly trpcService = inject(TRPCService);
	protected readonly authStateService = inject(AuthStateService);

	protected readonly tenantId = computed(() => {
		return this.authStateService.currentTenantId();
	});

	// Getter for today's date (as a string)
	protected get today(): string {
		return new Date().toDateString();
	}

	/**
	 * Constructor that accepts the table name and optionally an args object specifying whether
	 * multi-tenancy is enabled.
	 */
	constructor(
		public TABLE: any,
		public args: { hasTenant: boolean } = { hasTenant: true },
	) {
		super();
	}

	public get hasTenant(): boolean {
		return this.args.hasTenant;
	}

	/**
	 * Check if a result is a valid Entity (i.e. not undefined or null).
	 */
	public isEntity(result: any): result is Entity {
		return result !== undefined && result !== null;
	}

	/**
	 * Utility function to generate a random UUID using the uuid library.
	 */
	public randomUUID(): string {
		return uuid.v4();
	}

	// ───── SELECT QUERY PARSING & EMBEDDING ─────────────────────────────

	/**
	 * Build the select query string from the provided filters.
	 * It uses embedded table definitions if any active filter refers to an embedded table.
	 *
	 * @param filters - Optional filters input that might include embedded filter references.
	 * @returns A string representing the select query to be used in the service call.
	 */
	protected parsedSelectQueryFromFilters(
		filters?: FiltersInput<Entity>,
	): string {
		// Collect all active embedded table paths from any filter (including null filters).
		const activePaths = this.getActivePaths(filters);
		// Main query string (for example "*" or a list of columns from the main table)
		const mainQuery = this.selectQuery.query;
		// Recursively build the embedded select part based on active paths.
		const embeddedSelect = this.buildEmbedded(
			this.selectQuery.subtables,
			[],
			activePaths,
		);
		return embeddedSelect ? `${mainQuery}, ${embeddedSelect}` : mainQuery;
	}

	/**
	 * Recursively scans filters (including compound filters) to collect active embedded table paths.
	 * For example, a filter on "serial_products.pallets.udc" produces an array:
	 *   ["serial_products", "pallets"]
	 *
	 * @param filters - The filters object to scan.
	 * @returns An array of table paths (each path is an array of strings).
	 */
	private getActivePaths(filters?: FiltersInput<Entity>): string[][] {
		let paths: string[][] = [];
		if (filters?.filters) {
			for (const key in filters.filters) {
				const filter = filters.filters[key];
				paths = paths.concat(this._gatherActivePathsFromFilter(filter));
			}
		}
		return paths;
	}

	private getAllEmbeddedTableNames(query?: MainSelectQueryComponent): string[] {
		let names: string[] = [];
		if (!query || !query.subtables) return names;
		for (const sub of query.subtables) {
			names.push(sub.table);
			// Recursively add names of nested embedded tables.
			names = names.concat(
				this.getAllEmbeddedTableNames({
					query: sub.query,
					subtables: sub.subtables,
				}),
			);
		}
		return names;
	}

	/**
	 * Helper to gather the active embedded table paths from a single filter.
	 * If the filter is compound (with "or"/"and") then it processes each sub-filter.
	 *
	 * @param filter - The filter object to process.
	 * @returns An array of active embedded table path arrays.
	 */
	private _gatherActivePathsFromFilter(filter: any): string[][] {
		let collected: string[][] = [];
		if (!filter || !filter.active) return collected;

		// Handle NOT filters by processing its subfilter.
		if (filter.operator === "not") {
			collected = collected.concat(
				this._gatherActivePathsFromFilter(filter.value),
			);
			return collected;
		}

		// For compound filters like OR/AND, process each subfilter.
		if (filter.operator === "or" || filter.operator === "and") {
			let subFilters: any[] = [];
			if (Array.isArray(filter.value)) {
				subFilters = filter.value;
			} else if (filter.value && typeof filter.value === "object") {
				subFilters = Object.values(filter.value);
			}
			for (const subFilter of subFilters) {
				collected = collected.concat(
					this._gatherActivePathsFromFilter(subFilter),
				);
			}
			return collected;
		}

		// For a simple filter: if the column is written in dot notation, use its relationship parts.
		if (filter.column && typeof filter.column === "string") {
			if (filter.column.indexOf(".") !== -1) {
				const parts = filter.column.split(".");
				parts.pop(); // Remove the actual column name.
				collected.push(parts);
			} else {
				// Otherwise, check if the column name is one of our defined embedded table names.
				const embeddedNames = this.getAllEmbeddedTableNames(this.selectQuery);
				if (embeddedNames.indexOf(filter.column) !== -1) {
					// In this case, the entire column represents an embedded table.
					collected.push([filter.column]);
				}
			}
		}
		return collected;
	}

	/**
	 * Recursively builds the embedded select string based on the provided subtables.
	 * For each subtable, if its "apply" mode is "always" or if its table path is active (based on filters),
	 * then it is included in the embedded select string using PostgREST's embedded syntax.
	 *
	 * For example:
	 *   serial_products!inner(serial_number, batch, nestedEmbedded(...))
	 *
	 * @param subtables - An array of SubSelectQueryComponent to process.
	 * @param parentPath - The parent table path built so far.
	 * @param activePaths - All active embedded table paths derived from filters.
	 * @returns A string representing the embedded select portion.
	 */
	private buildEmbedded(
		subtables?: SubSelectQueryComponent[],
		parentPath: string[] = [],
		activePaths: string[][] = [],
	): string {
		if (!subtables) return "";
		const parts: string[] = [];
		for (const sub of subtables) {
			const currentPath = [...parentPath, sub.table];
			// Include this embedded table if its flag is "always" or an active filter affects it.
			if (
				sub.apply === "always" ||
				this.isPathActive(currentPath, activePaths)
			) {
				const nested = this.buildEmbedded(
					sub.subtables,
					currentPath,
					activePaths,
				);
				const queryParts = [];
				if (sub.query) {
					queryParts.push(sub.query);
				}
				if (nested) {
					queryParts.push(nested);
				}

				const query = queryParts.length > 0 ? queryParts.join(", ") : "*";
				// Use PostgREST embedded syntax: tableName!foreignKey!join(query, ...) or tableName!foreignKey(query, ...)
				let embeddedTableName = sub.table;

				// Add foreign key specification if provided
				if (sub.foreignKey) {
					embeddedTableName = `${sub.table}!${sub.foreignKey}`;
				}

				// Add join type if provided
				if (sub.join) {
					embeddedTableName = `${embeddedTableName}!${sub.join}`;
				}

				parts.push(`${embeddedTableName}(${query})`);
			}
		}
		return parts.join(", ");
	}

	/**
	 * Helper to determine if the specific table path is active.
	 *
	 * @param path - The table path to check (array of table names).
	 * @param activePaths - An array of active table paths.
	 * @returns True if one of the active paths starts with the given path.
	 */
	private isPathActive(path: string[], activePaths: string[][]): boolean {
		return activePaths.some(
			(ap) =>
				ap.length >= path.length &&
				ap.slice(0, path.length).join(".") === path.join("."),
		);
	}

	// ───── VALUE & OPERATOR FORMATTING HELPERS ─────────────────────────────

	/**
	 * Formats the filter value depending on the operator.
	 * For example, for "contains" and "in" operators, the value might need to be wrapped in braces or parentheses.
	 *
	 * @param operator - The filter operator.
	 * @param value - The value to format.
	 * @returns The formatted value as a string.
	 */
	private formatValueForOperator(operator: string, value: any): string | null {
		if (value === undefined) {
			throw new Error("Filtered value cannot be undefined");
		}

		if (value === null) {
			return value;
		}

		// Wrap values accordingly for "contains" or "containedBy" operators.
		if (operator === "contains" || operator === "containedBy") {
			if (Array.isArray(value)) {
				return `{${value.join(",")}}`;
			}
			if (typeof value === "object") {
				return `[${JSON.stringify(value)}]`;
			}
			return `{${value}}`;
		}
		// For the "in" operator, values should be presented in a parenthesized, comma-separated list.
		if (operator === "in") {
			if (Array.isArray(value)) {
				return `(${value.join(",")})`;
			}
			return `(${value})`;
		}
		// Default: return the value as a string.
		return value.toString();
	}

	/**
	 * Maps the custom operator names to the PostgREST operator shorthand.
	 * This helps convert operators like "contains" to "cs".
	 *
	 * @param operator - The operator to encode.
	 * @returns The encoded PostgREST operator.
	 */
	private encodePostgrestOperator(operator: string): string | null {
		switch (operator) {
			case "eq":
			case "neq":
			case "gt":
			case "lt":
			case "gte":
			case "lte":
			case "like":
			case "ilike":
			case "is":
			case "in":
				return operator;
			case "contains":
				return "cs";
			case "containedBy":
				return "cd";
			case "rangeGt":
				return "sr";
			case "rangeLt":
				return "sl";
			case "rangeGte":
				return "nxr";
			case "rangeLte":
				return "nxl";
			case "rangeAdjacent":
				return "adj";
			case "overlaps":
				return "ov";
			case "textSearch":
				return "fts";
			default:
				return operator; // For any unknown operator, return it unchanged.
		}
	}

	// ───── BUILDING POSTGREST FILTER CONDITIONS ─────────────────────────────

	/**
	 * Converts a given filter (or compound set of filters) into a PostgREST condition string.
	 * Compound filters (with "or", "and", "not") are handled recursively.
	 *
	 * For example, a simple filter such as:
	 *   { column: "serial_products.serial_number", operator: "ilike", value: "485" }
	 * might produce:
	 *   "serial_products.serial_number.ilike.485"
	 *
	 * NOTE: For compound filters the embedded table prefix is removed later if needed.
	 *
	 * @param filter - The filter to stringify.
	 * @returns The condition string or null if the filter is not valid.
	 */
	private stringifySingleSubCondition(
		filter: FilterType<Entity, keyof Entity>,
	): string | null {
		if (!filter || !filter.active) return null;

		// Handle compound conditions (OR/AND)
		if (filter.operator === "or" || filter.operator === "and") {
			let subFilters: FilterType<Entity, keyof Entity>[] = [];
			if (Array.isArray(filter.value)) {
				subFilters = filter.value;
			} else if (filter.value && typeof filter.value === "object") {
				subFilters = Object.values(filter.value);
			}
			const subConditions = subFilters
				.map((sf) => this.stringifySingleSubCondition(sf))
				.filter((cond) => cond !== null) as string[];
			return `${filter.operator}(${subConditions.join(",")})`;
		}

		// Handle NOT filters.
		if (filter.operator === "not") {
			const subFilter = filter.value as FilterTypeWithoutAggregation<
				Entity,
				keyof Entity
			>;
			const encodedOp = this.encodePostgrestOperator(subFilter.operator);
			const formattedVal = this.formatValueForOperator(
				subFilter.operator,
				subFilter.value,
			);
			// Example output: "serial_products.id.not.in.(...)"
			if ("column" in subFilter) {
				return `${<string>subFilter.column}.not.${encodedOp}.${formattedVal}`;
			}
		}

		// Handle simple operators.
		if ("column" in filter) {
			const column = filter.column as string;
			switch (filter.operator) {
				case "eq":
				case "neq":
				case "gt":
				case "lt":
				case "gte":
				case "lte":
				case "like":
				case "ilike":
				case "is":
					return `${column}.${filter.operator}.${filter.value}`;
				case "in":
					return `${column}.in.${this.formatValueForOperator(filter.operator, filter.value)}`;
				case "contains":
					return `${column}.cs.${this.formatValueForOperator(filter.operator, filter.value)}`;
				case "containedBy":
					return `${column}.cd.${this.formatValueForOperator(filter.operator, filter.value)}`;
				case "rangeGt":
				case "rangeLt":
				case "rangeGte":
				case "rangeLte":
				case "rangeAdjacent":
				case "overlaps":
					return `${column}.${this.encodePostgrestOperator(filter.operator)}.${filter.value}`;
				case "ilikeAllOf": {
					const values = filter.value as string[];
					const conditions = values.map((v) => `${column}.ilike.${v}`);
					return `and(${conditions.join(",")})`;
				}
				case "ilikeAnyOf": {
					const values = filter.value as string[];
					const conditions = values.map((v) => `${column}.ilike.${v}`);
					return `or(${conditions.join(",")})`;
				}
				case "likeAllOf": {
					const values = filter.value as string[];
					const conditions = values.map((v) => `${column}.like.${v}`);
					return `and(${conditions.join(",")})`;
				}
				case "likeAnyOf": {
					const values = filter.value as string[];
					const conditions = values.map((v) => `${column}.like.${v}`);
					return `or(${conditions.join(",")})`;
				}
				case "search":
					return `${column}.fts.${(filter.value as { query: string }).query}`;
				default:
			}
		}

		return null;
	}

	// ───── APPLYING FILTERS TO THE POSTGREST BUILDER ─────────────────────────────

	/**
	 * Applies filters to a provided PostgrestFilterBuilder instance.
	 * This function loops through each filter from the input and calls the corresponding
	 * method on the builder (e.g. eq, gt, ilike, etc.). It handles compound filters (or/and/not)
	 * as well as tenant filtering if multi-tenancy is enabled.
	 *
	 * @param data - The PostgrestFilterBuilder instance to apply filters to.
	 * @param input - The filters input containing filter definitions and pagination options.
	 * @returns The PostgrestFilterBuilder with all filters applied.
	 */
	public applyFilters(
		data: PostgrestFilterBuilder<
			Database["public"],
			Record<string, unknown>,
			any[],
			any,
			unknown
		>,
		input?: FiltersInput<Entity>,
	): PostgrestFilterBuilder<
		Database["public"],
		Record<string, unknown>,
		any[],
		any,
		unknown
	> {
		// Ensure we have a valid filters object
		let newInput = input;
		if (!newInput) {
			newInput = { filters: {} };
		}
		if (!newInput.filters) {
			newInput.filters = {};
		}

		// ─── HANDLE TENANT FILTERING ─────────────────────────────
		const tenantId = this.tenantId();
		if (this.hasTenant) {
			// Ensure tenant filtering is applied if multi-tenancy is enabled.
			if (!tenantId) {
				console.error(this.TABLE);
				this.loggerService.logError(`Tenant ID not set - ${this.TABLE}`);
				throw new Error("Tenant ID not set");
			}
			// Force a tenant_id filter.
			newInput.filters["tenant_id"] = <any>{
				active: true,
				column: "tenant_id",
				operator: "eq",
				value: tenantId,
			};
		}

		const d: any = data;

		// ─── APPLY EACH FILTER ─────────────────────────────────────
		if (input?.filters) {
			for (const key in input.filters) {
				const filter = newInput.filters[key] as any;
				if (!filter.active) continue;
				// Process filters based on their operator.
				switch (filter.operator) {
					case "eq":
						d.eq(filter.column, filter.value);
						break;
					case "neq":
						d.neq(filter.column, filter.value);
						break;
					case "gt":
						d.gt(filter.column, filter.value);
						break;
					case "lt":
						d.lt(filter.column, filter.value);
						break;
					case "gte":
						d.gte(filter.column, filter.value);
						break;
					case "lte":
						d.lte(filter.column, filter.value);
						break;
					case "like":
						d.like(filter.column, filter.value);
						break;
					case "ilike":
						d.ilike(filter.column, filter.value);
						break;
					case "ilikeAllOf":
						d.ilikeAllOf(filter.column, filter.value);
						break;
					case "ilikeAnyOf":
						d.ilikeAnyOf(filter.column, filter.value);
						break;
					case "likeAllOf":
						d.likeAllOf(filter.column, filter.value);
						break;
					case "likeAnyOf":
						d.likeAnyOf(filter.column, filter.value);
						break;
					case "is":
						d.is(filter.column, filter.value);
						break;
					case "in":
						d.in(filter.column, filter.value);
						break;
					case "contains":
						d.contains(
							filter.column,
							this.formatValueForOperator(filter.operator, filter.value),
						);
						break;
					case "containedBy":
						d.containedBy(filter.column, filter.value);
						break;
					case "rangeGt":
						d.rangeGt(filter.column, filter.value);
						break;
					case "rangeLt":
						d.rangeLt(filter.column, filter.value);
						break;
					case "rangeGte":
						d.rangeGte(filter.column, filter.value);
						break;
					case "rangeLte":
						d.rangeLte(filter.column, filter.value);
						break;
					case "rangeAdjacent":
						d.rangeAdjacent(filter.column, filter.value);
						break;
					case "overlaps":
						d.overlaps(filter.column, filter.value);
						break;
					case "textSearch":
						// Only apply text search if the query is non-empty.
						if (filter.value.query && filter.value.query.length > 0) {
							d.textSearch(filter.column, `'${filter.value.query}'`);
						}
						break;
					case "not": {
						// Top-level NOT filter: we process its subfilter.
						const subFilter = filter.value as FilterTypeWithoutAggregation<
							Entity,
							keyof Entity
						>;
						const encodedOp = this.encodePostgrestOperator(subFilter.operator);
						const formattedVal = this.formatValueForOperator(
							subFilter.operator,
							subFilter.value,
						);
						if (subFilter.column && encodedOp) {
							d.not(<string>subFilter.column, encodedOp, formattedVal);
						}
						break;
					}
					case "or": {
						// Also works for "and" if needed.
						let subfilters: FilterType<Entity, keyof Entity>[] = [];
						if (Array.isArray(filter.value)) {
							subfilters = filter.value;
						} else if (filter.value && typeof filter.value === "object") {
							subfilters = Object.values(filter.value);
						}
						const conditions = subfilters
							.map((sf) => this.stringifySingleSubCondition(sf))
							.filter((c) => c !== null) as string[];
						// Compute the common prefix from all subfilter columns (e.g., "serial_products.pallets")
						const columns = subfilters
							.map((sf) =>
								"column" in sf && typeof sf.column === "string"
									? sf.column
									: "",
							)
							.filter((col) => col.includes("."));
						let commonPrefix = "";
						if (columns.length > 0) {
							commonPrefix = this.computeCommonTablePrefix(columns);
						}
						if (commonPrefix) {
							const prefixWithDot = `${commonPrefix}.`;
							const strippedConditions = conditions.map((cond) =>
								cond.startsWith(prefixWithDot)
									? cond.substring(prefixWithDot.length)
									: cond,
							);
							d.or(strippedConditions.join(","), {
								foreignTable: commonPrefix,
								referencedTable: commonPrefix,
							});
						} else {
							d.or(conditions.join(","));
						}
						break;
					}
					// Other branch for "filter" operator.
					case "filter":
						d.filter(filter.column, filter.operator, filter.value);
						break;
					case "sort":
						// Apply ordering with sorting options.
						d.order(
							filter.column,
							filter.value.nullsFirst
								? {
									ascending: filter.value.asc,
									nullsFirst: filter.value.nullsFirst,
								}
								: {
									ascending: filter.value.asc,
								},
						);
						break;
					default:
						break;
				}
			}
		}

		// ─── SET PAGINATION RANGE ─────────────────────────────
		// Default pagination is from 0 to 1000 if not specified.
		d.range(input?.from ?? 0, input?.to ?? 1000);
		return d;
	}

	/**
	 * Helper function to compute the common table prefix from an array of fully-qualified column names.
	 * For example, given ["serial_products.pallets.udc", "serial_products.pallets.barcode"]
	 * it returns "serial_products.pallets".
	 *
	 * @param columns - Array of column names in dot notation.
	 * @returns The common prefix (table path) as a string.
	 */
	private computeCommonTablePrefix(columns: string[]): string {
		if (columns.length === 0) return "";
		let commonSegments = columns[0].split(".");
		for (let i = 1; i < columns.length; i++) {
			const segments = columns[i].split(".");
			let j = 0;
			while (
				j < commonSegments.length &&
				j < segments.length &&
				commonSegments[j] === segments[j]
			) {
				j++;
			}
			commonSegments = commonSegments.slice(0, j);
			if (commonSegments.length === 0) break;
		}
		return commonSegments.join(".");
	}

	// ───── END OF QUERY BUILDING FUNCTIONS ─────────────────────────────

	// ───── FUNCTIONS TO EXECUTE THE QUERIES (getMany, getCount, getById) ─────────────────────────────

	/**
	 * Returns a string of the SQL query and the count of the query.
	 * @param input - The filters and pagination information.
	 * @param fileName - The name of the file to export.
	 * @param overridenColumnsName - The columns to export.
	 * @param overrideExportPostgrestQuery - The postgrest query to use.
	 * @param overrideExportCount - The count of the query.
	 * @param hiddenExportColumns - The columns to hide in the export.
	 * @returns An observable emitting an object with the query and the count.
	 */
	public getManySqlExportQuery(
		input?: FiltersInput<Entity>,
		fileName?: string,
		overridenColumnsName?: Record<string, string>,
		overrideExportPostgrestQuery?: PostgrestFilterBuilder<
			Database["public"],
			any,
			any[],
			any,
			unknown
		>,
		overrideExportCount?: number,
		columnsToExport?: string[],
		hiddenExportColumns?: string[],
	): Observable<{
		url: string;
		id: string;
	}> {
		const newInput = structuredClone(input);
		console.log('0AAAA',
			newInput,
			fileName,
			overridenColumnsName,
			overrideExportPostgrestQuery,
			overrideExportCount,
			hiddenExportColumns,
		);
		if (newInput?.from) {
			delete newInput.from;
		}
		if (newInput?.to) {
			delete newInput.to;
		}
		return defer(async () => {
			console.log("START");
			const obj: {
				id: string;
				postgresQueryUrl: string;
				count: number;
			} = {
				id: uuid.v4(),
				postgresQueryUrl: "",
				count: overrideExportCount ?? -1,
			};
			// 1. Find the information of the current query
			if (overrideExportPostgrestQuery) {
				const postgrestQuery = overrideExportPostgrestQuery;
				const postgresQueryUrl = JSON.parse(
					JSON.stringify(postgrestQuery, null, 2),
				).url;
				obj.postgresQueryUrl = postgresQueryUrl;
			} else if (columnsToExport) {
				const columnsToExportString = columnsToExport.join(",");
				const selectQuery = this.supabaseClient
					.from(this.TABLE)
					.select(columnsToExportString)
				obj.count =
					overrideExportCount ?? (await lastValueFrom(this.getCount(newInput)));
				const postgrestQuery = this.applyFilters(selectQuery, newInput);
				obj.postgresQueryUrl = JSON.parse(
					JSON.stringify(postgrestQuery, null, 2),
				).url;
			} else {
				const selectQuery = this.supabaseClient
					.from(this.TABLE)
					.select(this.parsedSelectQueryFromFilters(newInput));
				obj.count =
					overrideExportCount ?? (await lastValueFrom(this.getCount(newInput)));
				const postgrestQuery = this.applyFilters(selectQuery, newInput);
				console.log('2AAAA', postgrestQuery);
				obj.postgresQueryUrl = JSON.parse(
					JSON.stringify(postgrestQuery, null, 2),
				).url;
			}
			/**
			 * Example of the url:
			 * "url": "articles_with_details?select=*&order=article_name.asc&total_serial_products=gte.2&tenant_id=eq.156ae918-02a2-459a-89d7-0d61d9d021cd&offset=0&limit=1001",
			 */

			// 2. Create the object in the report_exports table
			let reportExport = (
				await this.supabaseClient
					.from("report_exports")
					.insert({
						id: obj.id,
						entity_type: this.TABLE,
						tenant_id: this.tenantId() ?? "",
						status: "pending",
						file_name: fileName ?? `${this.TABLE}-${obj.id}.xlsx`,
						query_count: obj.count,
						overriden_columns_name: overridenColumnsName,
						query_url: obj.postgresQueryUrl,
						hidden_columns: hiddenExportColumns,
					})
					.select()
			).data?.[0] ?? null;

			// 3. Run the start export in fastify
			const data =
				await this.trpcService.client.importExports.exportExcel.mutate({
					id: obj.id,
				});

			console.log(data);

			// 4. Return the url of the file
			reportExport = (
				await this.supabaseClient
					.from("report_exports")
					.select("*")
					.eq("id", obj.id)
					.single()
			).data ?? null;

			console.log(reportExport);

			if (!reportExport) {
				throw new Error("Report export not found");
			}

			await lastValueFrom(
				this.downloadFile(
					reportExport.file_url || "",
					reportExport.file_name || "",
				),
			);

			// Apply filters to the query.
			return {
				url: reportExport.file_url || "",
				id: obj.id,
			};
		}).pipe(
			map((response) => {
				return response;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	public downloadFile(path: string, fileName: string): Observable<void> {
		const pathAfterImport = path.split("files/")[1];
		return from(
			this.supabaseClient.storage.from("files").download(pathAfterImport),
		).pipe(
			tap((res) => {
				if (res.data) {
					const url = window.URL.createObjectURL(res.data);
					const a = document.createElement("a");
					a.href = url;
					a.download = fileName;
					a.click();
					window.URL.revokeObjectURL(url);
				}
			}),
			map(() => { }),
		);
	}

	/**
	 * Returns a CSV string of the query.
	 * @param input - The filters and pagination information.
	 * @returns An observable emitting a CSV string.
	 */
	public getManyCSV(input?: FiltersInput<Entity>): Observable<string> {
		return defer(() => {
			// Build the select query including embedded table expressions.
			const selectQuery = this.supabaseClient
				.from(this.TABLE)
				.select(this.parsedSelectQueryFromFilters(input));

			// Apply filters to the query.
			return from(this.applyFilters(selectQuery, input).csv());
		}).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.data;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Returns an object containing an Observable for data and an Observable for the count,
	 * useful when both lists and counts are needed.
	 *
	 * @param input - Filters and pagination for the query.
	 * @returns An object with data and count observables.
	 */
	public getManyWithCount(input?: FiltersInput<Entity>): {
		data: Observable<Entity[]>;
		count: Observable<number>;
	} {
		const data = this.getMany(input);
		const count = this.getCount(input);
		return { data, count };
	}

	/**
	 * Returns all entities.
	 * @warning This method is not paginated and it can be very slow for large datasets.
	 * @param input - The filters and pagination information.
	 * @returns An observable emitting an array of entities.
	 */
	public getAll(input?: FiltersInput<Entity>): Observable<Entity[]> {
		if (this.hasTenant && !this.tenantId()) return of([]);

		return defer(async () => {
			const count = await lastValueFrom(this.getCount(input));

			const chunkSize = 500;
			const batchSize = chunkSize; // Use chunkSize as the batch size for reliable fetching
			const promises: Promise<Entity[]>[] = [];

			for (let i = 0; i < count; i += batchSize) {
				promises.push(
					lastValueFrom(
						// Convert Observable<Entity[]> to Promise<Entity[]>
						this.getMany({
							...input,
							from: i,
							to: i + batchSize, // Kept original logic: i + chunkSize * 3
							filters: input?.filters || {},
						}),
					),
				);
			}
			// Exec max 3 promises at a time	
			const maxConcurrentPromises = 3;
			const results = [];
			for (let i = 0; i < promises.length; i += maxConcurrentPromises) {
				const batch = promises.slice(i, i + maxConcurrentPromises);
				const batchResults = await Promise.all(batch);
				results.push(...batchResults);
			}

			// Flatten Entity[][] to Entity[]
			return results.flat();
		}).pipe(
			catchError((error) => this.errorHandler(error)), // Moved catchError to the outer Observable
		);
	}

	/**
	 * Executes a "get many" query using the generated select query string and applied filters.
	 *
	 * @param input - The filters and pagination information.
	 * @returns An observable emitting an array of entities.
	 */
	public getMany(input?: FiltersInput<Entity>): Observable<Entity[]> {
		if (this.hasTenant && !this.tenantId()) return of([]);

		return defer(() => {
			// Build the select query including embedded table expressions.
			const selectQuery = this.supabaseClient
				.from(this.TABLE)
				.select(this.parsedSelectQueryFromFilters(input));
			// Apply filters to the query.
			const filteredQuery = this.applyFilters(selectQuery, input);
			return from(filteredQuery);
		}).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.data;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Executes a "get count" query by performing a head request and extracting the count.
	 *
	 * @param input - The filters and pagination information.
	 * @returns An observable emitting the count of entities.
	 */
	public getCount(input?: FiltersInput<Entity>): Observable<number> {
		if (this.hasTenant && !this.tenantId()) return of(0);

		return defer(() => {
			const selectQuery = this.supabaseClient
				.from(this.TABLE)
				.select(this.parsedSelectQueryFromFilters(input), {
					count: "exact",
					head: true,
				});
			return from(this.applyFilters(selectQuery, input));
		}).pipe(
			map((response) => {
				if (response.error) throw response.error;
				return response.count ?? 0;
			}),
			catchError((error) => this.errorHandler(error)),
		);
	}

	/**
	 * Retrieves a single entity by its id. Applies a filter on "id" to narrow down the results.
	 *
	 * @param id - The id of the entity.
	 * @param input - Optional filters to apply.
	 * @returns An observable emitting the single entity or throwing an error if not found.
	 */
	public getById(
		id: string | number,
		input?: FiltersInput<Entity>,
	): Observable<Entity> {
		return defer(() => {
			// If no input is provided, initialize a filter for the id.
			if (!input) {
				input = {
					from: 0,
					to: 0,
					filters: <any>{
						id: { active: true, operator: "eq", column: "id", value: id },
					},
				};
			} else {
				// Otherwise, override/ensure the id filter.
				input.from = 0;
				input.to = 0;
				input.filters = {
					...input.filters,
					id: <any>{ active: true, operator: "eq", column: "id", value: id },
				};
			}
			return this.getMany(input);
		}).pipe(
			map((response) => {
				if (response.length === 0) throw new Error("Element not found");
				return response[0];
			}),
		);
	}
}
